/** 节点配置 UI 字段（JSON 字符串），提交时再组装 headGroupSpec / workerGroupSpecs */
import type { FormInstance } from '@arco-design/web-react/es/Form';
import { cloneDeep, get, pick, set } from 'lodash';

import {
  hasMeaningfulHealthCheckConfig,
  healthCheckFromLivenessProbe,
  readHealthCheckFromFormValues,
  resolveEmbeddedHealthCheckForSubmit,
} from '@/delivery-type-plugin/capabilities/health-check';
import type { HealthCheckFormValues } from '@/delivery-type-plugin/capabilities/health-check/types';
import type {
  MaasGroupSpec,
  MaasInferenceServiceFormItem,
  MaasRunMode,
  MaasSharedFormValues,
  MaasSystemDiskSizes,
} from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { MaasInferenceSource, MaasRunMode as RunMode } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { toFormJsonString } from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';
import { parseFormJsonField } from '@/delivery-type-plugin/capabilities/maas-init/utils/jsonFormField';
import { normalizeSystemVolumeForSubmit } from '@/delivery-type-plugin/capabilities/maas-model-storage/storageVolume';
import {
  DEFAULT_SYSTEM_VOLUME,
  readGroupSystemDiskSizeGi,
  stripStorageFromGroupSpec,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/masterGroupForm';
import {
  isPersistedSystemImageMatchingCatalog,
  resolveInferenceSourceFromSystem,
  resolveMaasInferenceSourceForForm,
  stripCatalogInferenceMetadataFromSystem,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/payload';
import { mergeMaasGroupSystemForApi } from '@/delivery-type-plugin/capabilities/maas-node-groups/payload';
import { healthCheckToK8sLivenessProbe } from '@/delivery-type-plugin/capabilities/maas-system-config/deployConfigPayload';
import {
  buildImageItemFromInferenceSystem,
  parseInferenceFrameworkImageItem,
} from '@/delivery-type-plugin/capabilities/maas-system-config/presetInferenceImage';
import {
  hasMeaningfulSharedMemoryConfig,
  readSharedMemoryFromFormValues,
  sharedMemoryFromSystemDevices,
} from '@/delivery-type-plugin/capabilities/shared-memory';
import type { SharedMemoryFormValues } from '@/delivery-type-plugin/capabilities/shared-memory/types';
import { clearSystemDiskFields } from '@/delivery-type-plugin/capabilities/system-disk-size';

import { normalizeMaasGroupForNodeFields, normalizeMaasInferenceSystemForForm } from './groupFormNormalize';
import {
  DEFAULT_SYSTEM_DISK_SIZE_GI,
  INFERENCE_SERVICE_FIELD,
  INFERENCE_SYSTEM_FIELD,
  MASTER_NODES_FIELD,
  MASTER_SYSTEM_DISK_FIELD,
  MASTER_SYSTEM_DISK_PRICING_FIELD,
  WORKER_NODES_FIELD,
  WORKER_SYSTEM_DISK_FIELD,
  WORKER_SYSTEM_DISK_PRICING_FIELD,
} from './nodeFieldNames';
import { DEFAULT_MAAS_NODE_GROUP_FIELD_PATHS, type MaasNodeGroupFieldPaths } from './nodeGroupFieldPaths';

function trimSystemArchitecture(value: unknown): string | undefined {
  const trimmed = String(value ?? '').trim();
  return trimmed || undefined;
}

/**
 * 合并 inferenceSystem 时保留节点弹窗手动选择的 architecture。
 * 镜像 cpuArchitectures 为空时 applyContainerImageItemToForm 会写入 architecture: ''，
 * 直接 Object.assign 会覆盖用户在节点配置里选的值，提交时 normalize 又会剔除空串。
 */
function mergeInferenceSystemForGroup(
  nodeSystem: Record<string, unknown>,
  inferenceSystem: Record<string, unknown>,
): Record<string, unknown> {
  const inferenceCopy = cloneDeep(inferenceSystem);
  const nodeArch = trimSystemArchitecture(nodeSystem.architecture);
  const inferenceArch = trimSystemArchitecture(inferenceCopy.architecture);
  if (!inferenceArch && nodeArch) {
    inferenceCopy.architecture = nodeArch;
  } else if (!inferenceArch) {
    delete inferenceCopy.architecture;
  }
  return inferenceCopy;
}

/** 主组节点保存后，将手动选择的 architecture 同步到 _inferenceSystem（供提交与再次打开弹窗） */
export function syncInferenceSystemArchitectureFromNode(form: FormInstance, nodeSystem?: Record<string, unknown>): void {
  const architecture = trimSystemArchitecture(nodeSystem?.architecture);
  if (!architecture) return;
  form.setFieldValue(`${INFERENCE_SYSTEM_FIELD}.architecture`, architecture);
}

/**
 * 提交前剔除 UI 专用字段。
 * - 默认：剥离 usageScenario（应用实例等旧路径仍写组内 service）
 * - keepUsage：将 usageScenario 映射为 API 字段 usage（模型实例顶层 network.service）
 */
export function sanitizeInferenceServiceForApi(
  service: unknown,
  options?: { keepUsage?: boolean },
): (MaasInferenceServiceFormItem & { usage?: string })[] | undefined {
  if (!Array.isArray(service)) return undefined;
  return service.map((raw) => {
    if (!raw || typeof raw !== 'object') return raw as MaasInferenceServiceFormItem;
    const { _isDefault, usageScenario, ...rest } = raw as MaasInferenceServiceFormItem;
    if (options?.keepUsage) {
      const usage = String(usageScenario ?? '').trim();
      return usage ? { ...rest, usage } : { ...rest };
    }
    return rest;
  });
}

/** 表单未写入时补全系统盘默认值（legacy Slider；新卡片空态不预填，勿在 MaasSystemDiskBlock 调用） */
export function ensureSystemDiskSizeOnForm(form: FormInstance, field: string, defaultGi: number = DEFAULT_SYSTEM_DISK_SIZE_GI): void {
  const value = form.getFieldValue(field);
  if (value === undefined || value === null || (typeof value === 'number' && Number.isNaN(value))) {
    form.setFieldValue(field, defaultGi);
  }
}

/** 清空主/Worker 系统盘容量与计价（对齐 CI：回空态，不写默认 10Gi） */
export function clearMaasSystemDiskFieldsOnForm(form: FormInstance): void {
  clearSystemDiskFields(form, { size: MASTER_SYSTEM_DISK_FIELD, pricing: MASTER_SYSTEM_DISK_PRICING_FIELD });
  clearSystemDiskFields(form, { size: WORKER_SYSTEM_DISK_FIELD, pricing: WORKER_SYSTEM_DISK_PRICING_FIELD });
}

/** 节点 JSON 中保留的 system 字段（不含推理镜像；含节点级架构/启动命令/环境变量） */
const NODE_SYSTEM_KEYS = ['architecture', 'env', 'command', 'args', 'registry'] as const;

/** embedded 模式：节点 JSON 上仅 UI 使用的扩展字段 */
export const EMBEDDED_NODE_UI_KEYS = [
  '_containerDeployHealthCheck',
  '_containerDeploySharedMemory',
  '_healthCheckEnabled',
  '_sharedMemoryEnabled',
  '_nicEnabled',
  '_systemDiskSize',
  '_systemDiskPricing',
  'maasInferenceSource',
  'inferenceFrameworkImage',
] as const;

export interface EmbeddedNodeSpec extends MaasGroupSpec {
  _containerDeployHealthCheck?: Record<string, unknown>;
  _containerDeploySharedMemory?: SharedMemoryFormValues;
  /** 节点抽屉：勾选后才展示/提交探活 */
  _healthCheckEnabled?: boolean;
  /** 节点抽屉：勾选后才展示/提交共享内存 */
  _sharedMemoryEnabled?: boolean;
  _nicEnabled?: boolean;
  /** 各节点独立系统盘容量（Gi） */
  _systemDiskSize?: number;
  _systemDiskPricing?: Record<string, unknown>;
  /** 抽屉回显：推理框架来源 */
  maasInferenceSource?: MaasInferenceSource;
  /** 抽屉回显：平台内置镜像 catalog 快照 */
  inferenceFrameworkImage?: string;
}

export interface EnrichEmbeddedNodeInferenceEchoOptions {
  annotationInferenceFrameworkImage?: unknown;
  containerDeployConfig?: unknown;
}

/** 从 system / annotation 补全 embedded 节点镜像回显字段（详情回填与抽屉重开） */
export function enrichEmbeddedNodeInferenceEcho(
  node: EmbeddedNodeSpec,
  options?: EnrichEmbeddedNodeInferenceEchoOptions,
): EmbeddedNodeSpec {
  const system = node.system as Record<string, unknown> | undefined;
  if (!system) return node;

  let inferenceFrameworkImageFromAnnotation: string | undefined;
  if (!node.inferenceFrameworkImage) {
    const catalogFromAnnotation = parseInferenceFrameworkImageItem(options?.annotationInferenceFrameworkImage);
    if (catalogFromAnnotation && isPersistedSystemImageMatchingCatalog(system, catalogFromAnnotation)) {
      inferenceFrameworkImageFromAnnotation =
        typeof options?.annotationInferenceFrameworkImage === 'string'
          ? options.annotationInferenceFrameworkImage
          : toFormJsonString(catalogFromAnnotation);
    }
  }

  const maasInferenceSource =
    node.maasInferenceSource ??
    resolveMaasInferenceSourceForForm({
      inferenceFrameworkImage: node.inferenceFrameworkImage ?? inferenceFrameworkImageFromAnnotation,
      _inferenceSystem: system,
    });

  let inferenceFrameworkImage = node.inferenceFrameworkImage ?? inferenceFrameworkImageFromAnnotation;
  if (!inferenceFrameworkImage && maasInferenceSource !== MaasInferenceSource.Custom) {
    const imageItem = buildImageItemFromInferenceSystem({
      ...system,
      options: {
        ...((system.options as Record<string, unknown>) || {}),
        ...(options?.containerDeployConfig != null ? { containerDeployConfig: options.containerDeployConfig } : {}),
      },
    });
    if (imageItem) {
      inferenceFrameworkImage = toFormJsonString(imageItem);
    }
  }

  return {
    ...node,
    ...(inferenceFrameworkImage ? { inferenceFrameworkImage } : {}),
    maasInferenceSource,
  };
}

/** 读取 embedded 节点 JSON 上的独立系统盘容量（Gi） */
export function readEmbeddedNodeSystemDiskGi(node?: MaasGroupSpec): number | undefined {
  const raw = (node as EmbeddedNodeSpec | undefined)?._systemDiskSize;
  return typeof raw === 'number' && raw > 0 ? raw : undefined;
}

/** 批量补全 masterNodes / workerNodes JSON 字符串内的镜像回显字段 */
export function enrichEmbeddedNodesFieldJson(nodesRaw: unknown, options?: EnrichEmbeddedNodeInferenceEchoOptions): string {
  const nodes = parseNodesField(nodesRaw) as EmbeddedNodeSpec[];
  if (!nodes.length) return typeof nodesRaw === 'string' ? nodesRaw : JSON.stringify([]);
  return JSON.stringify(nodes.map((node) => enrichEmbeddedNodeInferenceEcho(node, options)));
}

export interface AssembleNodeFieldsOptions {
  /** 公共 env / 服务提升到顶层 spec；默认 false（训练集群等仍把 commonEnv 写入各组 system.env） */
  elevateSharedEnvAndService?: boolean;
  /** 模型实例 / 训练集群：镜像、服务、探活、网卡写入各节点 JSON */
  embeddedNodeConfig?: boolean;
  /** 节点 JSON / runMode / 系统盘字段路径；默认 masterNodes / workerNodes */
  fieldPaths?: MaasNodeGroupFieldPaths;
}

/** 节点探活模块是否启用（勾选或历史数据无显式 flag 时按内容推断） */
export function resolveEmbeddedHealthCheckEnabled(node?: EmbeddedNodeSpec | null): boolean {
  if (!node) return false;
  if (node._healthCheckEnabled === false) return false;
  if (node._healthCheckEnabled === true) return true;
  return hasMeaningfulHealthCheckConfig(readHealthCheckFromFormValues({ _containerDeployHealthCheck: node._containerDeployHealthCheck }));
}

/** 节点共享内存模块是否启用（勾选或历史数据无显式 flag 时按内容推断） */
export function resolveEmbeddedSharedMemoryEnabled(node?: EmbeddedNodeSpec | null): boolean {
  if (!node) return false;
  if (node._sharedMemoryEnabled === false) return false;
  if (node._sharedMemoryEnabled === true) return true;
  return hasMeaningfulSharedMemoryConfig(
    readSharedMemoryFromFormValues({ _containerDeploySharedMemory: node._containerDeploySharedMemory }),
  );
}

function readEmbeddedUiFields(
  group: MaasGroupSpec,
): Pick<
  EmbeddedNodeSpec,
  '_containerDeployHealthCheck' | '_containerDeploySharedMemory' | '_healthCheckEnabled' | '_sharedMemoryEnabled' | '_nicEnabled'
> {
  const raw = group as EmbeddedNodeSpec;
  const result: Pick<
    EmbeddedNodeSpec,
    '_containerDeployHealthCheck' | '_containerDeploySharedMemory' | '_healthCheckEnabled' | '_sharedMemoryEnabled' | '_nicEnabled'
  > = {};
  if (raw._containerDeployHealthCheck !== undefined) {
    result._containerDeployHealthCheck = cloneDeep(raw._containerDeployHealthCheck);
  }
  if (raw._containerDeploySharedMemory !== undefined) {
    result._containerDeploySharedMemory = cloneDeep(raw._containerDeploySharedMemory);
  }
  if (raw._healthCheckEnabled !== undefined) {
    result._healthCheckEnabled = raw._healthCheckEnabled;
  }
  if (raw._sharedMemoryEnabled !== undefined) {
    result._sharedMemoryEnabled = raw._sharedMemoryEnabled;
  }
  if (raw._nicEnabled !== undefined) {
    result._nicEnabled = raw._nicEnabled;
  }
  return result;
}

/** embedded：从 API 组 spec 拆出完整节点配置 */
export function extractEmbeddedNodeConfigFromGroup(group?: MaasGroupSpec): EmbeddedNodeSpec | undefined {
  if (!group) return undefined;
  const node = stripStorageFromGroupSpec(cloneDeep(group))! as EmbeddedNodeSpec;
  const system = node.system as Record<string, unknown> | undefined;
  if (system?.livenessProbe) {
    const healthCheck = healthCheckFromLivenessProbe(system.livenessProbe as Record<string, unknown>);
    if (healthCheck) {
      node._containerDeployHealthCheck = healthCheck as Record<string, unknown>;
      node._healthCheckEnabled = node._healthCheckEnabled ?? true;
    }
    const nextSystem = { ...system };
    delete nextSystem.livenessProbe;
    node.system = Object.keys(nextSystem).length ? nextSystem : undefined;
  }
  const devices = system?.devices as Record<string, unknown> | undefined;
  const sharedMemory = sharedMemoryFromSystemDevices(devices);
  if (sharedMemory) {
    node._containerDeploySharedMemory = sharedMemory;
    node._sharedMemoryEnabled = node._sharedMemoryEnabled ?? true;
    if (node.system && typeof node.system === 'object') {
      const nextSystem = { ...(node.system as Record<string, unknown>) };
      const nextDevices = { ...((nextSystem.devices as Record<string, unknown>) || {}) };
      delete nextDevices.sharedMemory;
      if (Object.keys(nextDevices).length) {
        nextSystem.devices = nextDevices;
      } else {
        delete nextSystem.devices;
      }
      node.system = Object.keys(nextSystem).length ? nextSystem : undefined;
    }
  }
  const network = get(node, 'resource.network') as Record<string, unknown> | undefined;
  if (network && 'service' in network) {
    delete network.service;
    if (!Object.keys(network).length) {
      delete (node.resource as Record<string, unknown>).network;
    }
  }
  let nicList = get(node, 'resource.nics');
  if (nicList == null) {
    nicList = get(node, 'resource.network.nics');
    if (nicList != null && node.resource) {
      (node.resource as Record<string, unknown>).nics = nicList;
      const legacyNetwork = get(node, 'resource.network') as Record<string, unknown> | undefined;
      if (legacyNetwork) {
        const nextNetwork = { ...legacyNetwork };
        delete nextNetwork.nics;
        if (Object.keys(nextNetwork).length) {
          (node.resource as Record<string, unknown>).network = nextNetwork;
        } else {
          delete (node.resource as Record<string, unknown>).network;
        }
      }
    }
  }
  const enabledNics = Array.isArray(nicList) ? nicList : [];
  if (enabledNics.length > 0) {
    node._nicEnabled = node._nicEnabled ?? true;
  }
  const systemDiskGi = readGroupSystemDiskSizeGi(group);
  if (systemDiskGi != null && systemDiskGi > 0) {
    node._systemDiskSize = systemDiskGi;
  }
  return enrichEmbeddedNodeInferenceEcho(node);
}

/** embedded：提交前合并公共 env、清洗 service / nics，剥离 UI 字段 */
export function mergeEmbeddedNodeForSubmit(
  node: EmbeddedNodeSpec | undefined,
  options?: {
    defaultName?: string;
    commonEnv?: { name: string; value?: string }[];
    /** 为 true 时公共 env 不写入组内（提升到顶层 environment） */
    elevateSharedEnvAndService?: boolean;
  },
): MaasGroupSpec {
  const base = cloneDeep(node) || ({ name: options?.defaultName || 'worker', replicas: 1 } as EmbeddedNodeSpec);
  const inferenceSource = base.maasInferenceSource ?? resolveInferenceSourceFromSystem(base.system as Record<string, unknown> | undefined);
  if (inferenceSource === MaasInferenceSource.Custom && base.system) {
    base.system = stripCatalogInferenceMetadataFromSystem(base.system as Record<string, unknown>);
  }
  const elevate = options?.elevateSharedEnvAndService === true;
  const nodeEnv = ((base.system?.env || []) as { name: string; value: string }[]).filter((item) => item?.name);
  const envmap: Record<string, string> = {};
  if (!elevate) {
    options?.commonEnv?.forEach((item) => {
      if (item?.name) envmap[item.name] = String(item.value ?? '');
    });
  }
  nodeEnv.forEach((item) => {
    envmap[item.name] = item.value;
  });
  const mergedEnv = Object.entries(envmap).map(([name, value]) => ({ name, value }));
  if (base.system) {
    const system = { ...(base.system as Record<string, unknown>) };
    if (mergedEnv.length) system.env = mergedEnv;
    else delete system.env;
    base.system = Object.keys(system).length ? system : undefined;
  } else if (mergedEnv.length) {
    base.system = { env: mergedEnv };
  }

  const resource = { ...((base.resource || {}) as Record<string, unknown>) };
  const network = { ...((resource.network as Record<string, unknown>) || {}) };
  delete network.service;
  if (resource.nics == null && network.nics != null) {
    resource.nics = network.nics;
  }
  delete network.nics;
  if (base._nicEnabled === false) {
    delete resource.nics;
  }
  if (Object.keys(network).length) {
    resource.network = network;
  } else {
    delete resource.network;
  }
  if (Object.keys(resource).length) {
    base.resource = resource as MaasGroupSpec['resource'];
  } else {
    delete base.resource;
  }

  const healthCheckEnabled = resolveEmbeddedHealthCheckEnabled(base);
  const sharedMemoryEnabled = resolveEmbeddedSharedMemoryEnabled(base);
  const healthCheck = healthCheckEnabled
    ? resolveEmbeddedHealthCheckForSubmit(base._containerDeployHealthCheck as HealthCheckFormValues | undefined)
    : undefined;
  const sharedMemory = sharedMemoryEnabled
    ? readSharedMemoryFromFormValues({
        _containerDeploySharedMemory: base._containerDeploySharedMemory,
      })
    : undefined;
  const systemWithAdvanced = { ...((base.system || {}) as Record<string, unknown>) };
  if (healthCheck && typeof healthCheck === 'object') {
    const livenessProbe = healthCheckToK8sLivenessProbe(healthCheck as Parameters<typeof healthCheckToK8sLivenessProbe>[0]);
    if (livenessProbe) {
      systemWithAdvanced.livenessProbe = livenessProbe;
    } else {
      delete systemWithAdvanced.livenessProbe;
    }
  } else {
    delete systemWithAdvanced.livenessProbe;
  }
  const existingDevices = { ...((systemWithAdvanced.devices as Record<string, unknown>) || {}) };
  if (sharedMemoryEnabled && hasMeaningfulSharedMemoryConfig(sharedMemory) && sharedMemory?.sizeLimit) {
    existingDevices.sharedMemory = { size: String(sharedMemory.sizeLimit).trim() };
  } else {
    delete existingDevices.sharedMemory;
  }
  if (Object.keys(existingDevices).length) {
    systemWithAdvanced.devices = existingDevices;
  } else {
    delete systemWithAdvanced.devices;
  }
  base.system = Object.keys(systemWithAdvanced).length ? systemWithAdvanced : undefined;

  const systemDiskGi = typeof base._systemDiskSize === 'number' && base._systemDiskSize > 0 ? base._systemDiskSize : undefined;

  EMBEDDED_NODE_UI_KEYS.forEach((key) => {
    delete (base as unknown as Record<string, unknown>)[key];
  });

  const result = stripStorageFromGroupSpec(base)!;
  if (systemDiskGi) {
    set(result, 'resource.storage.volume', [normalizeSystemVolumeForSubmit(cloneDeep(DEFAULT_SYSTEM_VOLUME), systemDiskGi)]);
  }
  return result;
}

/** 从 masterNodes / workerNodes 聚合各节点网卡（SKU 计价） */
export function collectEmbeddedNicsFromNodeFields(values: {
  masterNodes?: unknown;
  workerNodes?: unknown;
  gatewayNodes?: unknown;
}): unknown[] {
  const nodes = [
    ...parseNodesField(values.masterNodes),
    ...parseNodesField(values.workerNodes),
    ...parseNodesField(values.gatewayNodes),
  ] as EmbeddedNodeSpec[];
  return nodes.flatMap((node) => {
    if (node._nicEnabled === false) return [];
    const nics = get(node, 'resource.nics') ?? get(node, 'resource.network.nics');
    return Array.isArray(nics) ? nics : [];
  });
}

/** 清空各节点 JSON 内嵌网卡（部署模式切换等场景） */
export function clearEmbeddedNicsInNodeFields(form: FormInstance) {
  const clearField = (field: string) => {
    const nodes = parseNodesField(form.getFieldValue(field)) as EmbeddedNodeSpec[];
    if (!nodes.length) return;
    const next = nodes.map((node) => {
      const clone = cloneDeep(node);
      clone._nicEnabled = false;
      if (!clone.resource) return clone;
      const resource = { ...(clone.resource as Record<string, unknown>) };
      delete resource.nics;
      const network = resource.network as Record<string, unknown> | undefined;
      if (network) {
        const nextNetwork = { ...network };
        delete nextNetwork.nics;
        if (Object.keys(nextNetwork).length) resource.network = nextNetwork;
        else delete resource.network;
      }
      return { ...clone, resource: Object.keys(resource).length ? (resource as typeof clone.resource) : undefined };
    });
    writeNodesField(form, field, next);
  };
  clearField(MASTER_NODES_FIELD);
  clearField(WORKER_NODES_FIELD);
}

/** 解析表单中的节点 JSON 字段 */
export function parseNodesField(raw: unknown): MaasGroupSpec[] {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) return cloneDeep(raw) as MaasGroupSpec[];
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (cloneDeep(parsed) as MaasGroupSpec[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

/** 写入节点 JSON 字段 */
export function writeNodesField(form: FormInstance, field: string, nodes: MaasGroupSpec[]) {
  form.setFieldValue(field, JSON.stringify(nodes));
}

/** 切换推理/应用镜像时清空节点组，使节点弹窗按新 containerDeployConfig 重新初始化 */
export function resetMaasNodeGroupsOnForm(form: FormInstance) {
  writeNodesField(form, MASTER_NODES_FIELD, []);
  writeNodesField(form, WORKER_NODES_FIELD, []);
  clearMaasSystemDiskFieldsOnForm(form);
  form.setFields({
    [MASTER_NODES_FIELD]: { error: undefined },
    [WORKER_NODES_FIELD]: { error: undefined },
  });
}

/** 从完整组 spec 拆出节点配置（去掉推理镜像与服务端口，保留 initCommand / 环境变量） */
export function extractNodeConfigFromGroup(group?: MaasGroupSpec): MaasGroupSpec | undefined {
  if (!group) return undefined;
  const node = stripStorageFromGroupSpec(cloneDeep(group))!;
  const system = node.system as Record<string, unknown> | undefined;
  if (system) {
    const nodeSystem = pick(system, NODE_SYSTEM_KEYS);
    if (Object.keys(nodeSystem).length) {
      node.system = nodeSystem;
    } else {
      delete node.system;
    }
  }
  if (!node.initCommand) {
    delete node.initCommand;
  }
  if (node.resource) {
    const resource = { ...(node.resource as Record<string, unknown>) };
    const network = resource.network as Record<string, unknown> | undefined;
    if (network && 'service' in network) {
      const nextNetwork = { ...network };
      delete nextNetwork.service;
      if (Object.keys(nextNetwork).length) {
        resource.network = nextNetwork;
      } else {
        delete resource.network;
      }
    }
    if (Object.keys(resource).length) {
      node.resource = resource as MaasGroupSpec['resource'];
    } else {
      delete node.resource;
    }
  }
  return node;
}

/** 从主组 spec 提取推理 system（镜像等）与服务端口 */
export function extractInferenceFromGroup(group?: MaasGroupSpec): {
  inferenceSystem?: Record<string, unknown>;
  inferenceService?: unknown;
} {
  if (!group) return {};
  const system = group.system as Record<string, unknown> | undefined;
  if (!system) {
    return { inferenceService: get(group, 'resource.network.service') };
  }
  const inferenceSystem = cloneDeep(system);
  NODE_SYSTEM_KEYS.forEach((key) => {
    if (key === 'architecture') return;
    delete inferenceSystem[key];
  });
  return {
    inferenceSystem: Object.keys(inferenceSystem).length ? inferenceSystem : undefined,
    inferenceService: get(group, 'resource.network.service'),
  };
}

/** 合并节点配置与推理/服务字段为组 spec（不含 storage） */
function mergeNodeWithInference(
  node: MaasGroupSpec | undefined,
  options: {
    inferenceSystem?: Record<string, unknown>;
    inferenceService?: unknown;
    withInference?: boolean;
    defaultName?: string;
    commonEnv?: any[];
    /** 为 true 时公共 env / 服务不写入组内（提升到顶层 environment / network） */
    elevateSharedEnvAndService?: boolean;
  },
): MaasGroupSpec {
  const elevate = options.elevateSharedEnvAndService === true;
  const base: MaasGroupSpec = {
    name: node?.name || options.defaultName || 'worker',
    replicas: node?.replicas ?? 1,
    ...(node?.initCommand ? { initCommand: cloneDeep(node.initCommand) } : {}),
    ...(node?.resource ? { resource: cloneDeep(node.resource) } : {}),
  };
  const nodeSystem = (node?.system || {}) as Record<string, unknown>;
  if (node?.registry && !nodeSystem.registry) {
    nodeSystem.registry = cloneDeep(node.registry);
  }
  const nodeEnv = ((node?.system?.env || []) as { name: string; value: string }[]).filter((item) => item?.name);
  const envmap: Record<string, string> = {};
  if (!elevate) {
    options?.commonEnv?.forEach((item) => {
      if (item?.name) envmap[item.name] = item.value;
    });
  }
  nodeEnv.forEach((item) => {
    envmap[item.name] = item.value;
  });
  const mergedSystem: Record<string, unknown> = { ...nodeSystem };
  const mergedEnv = Object.entries(envmap).map(([name, value]) => ({ name, value }));
  if (mergedEnv.length) {
    mergedSystem.env = mergedEnv;
  } else {
    delete mergedSystem.env;
  }
  if (options.withInference && options.inferenceSystem) {
    const merged = mergeMaasGroupSystemForApi(Object.keys(mergedSystem).length ? mergedSystem : undefined, options.inferenceSystem);
    if (merged) {
      Object.keys(mergedSystem).forEach((key) => delete mergedSystem[key]);
      Object.assign(mergedSystem, merged);
    } else {
      Object.assign(mergedSystem, mergeInferenceSystemForGroup(nodeSystem, options.inferenceSystem));
    }
    // 推理 system 合并后重新套用 env（避免 inference overlay 带入公共 env）
    if (mergedEnv.length) {
      mergedSystem.env = mergedEnv;
    } else {
      delete mergedSystem.env;
    }
  }
  if (Object.keys(mergedSystem).length) {
    base.system = mergedSystem;
  }
  if (options.withInference && options.inferenceService && !elevate) {
    const network = { ...((base.resource?.network as Record<string, unknown>) || {}) };
    network.service = sanitizeInferenceServiceForApi(options.inferenceService);
    base.resource = { ...(base.resource || {}), network };
  } else if (elevate && base.resource?.network && 'service' in (base.resource.network as object)) {
    const network = { ...(base.resource.network as Record<string, unknown>) };
    delete network.service;
    if (Object.keys(network).length) {
      base.resource = { ...base.resource, network };
    } else {
      const nextResource = { ...(base.resource as Record<string, unknown>) };
      delete nextResource.network;
      base.resource = Object.keys(nextResource).length ? (nextResource as MaasGroupSpec['resource']) : undefined;
    }
  }
  return stripStorageFromGroupSpec(base)!;
}

/** 详情/编辑回填：将 API 组结构拆成 masterNodes / workerNodes JSON 与推理字段 */
export function splitGroupsToNodeFields(
  spec: {
    runMode?: MaasRunMode;
    headGroupSpec?: MaasGroupSpec;
    workerGroupSpecs?: MaasGroupSpec[];
  },
  options?: Pick<AssembleNodeFieldsOptions, 'embeddedNodeConfig'>,
): {
  masterNodes: MaasGroupSpec[];
  workerNodes: MaasGroupSpec[];
  inferenceSystem?: Record<string, unknown>;
  inferenceService?: unknown;
} {
  const runMode = spec.runMode;
  const workers = spec.workerGroupSpecs ?? [];
  let inferenceSystem: Record<string, unknown> | undefined;
  let inferenceService: unknown;

  if (options?.embeddedNodeConfig) {
    const extract = (group?: MaasGroupSpec) => {
      const node = extractEmbeddedNodeConfigFromGroup(group);
      return node ? [node] : [];
    };
    if (runMode === RunMode.PrimarySecondary) {
      return {
        masterNodes: extract(spec.headGroupSpec),
        workerNodes: workers.flatMap((w) => extract(w)),
      };
    }
    return {
      masterNodes: [],
      workerNodes: workers.flatMap((w) => extract(w)),
    };
  }

  if (runMode === RunMode.PrimarySecondary) {
    let headInference = extractInferenceFromGroup(spec.headGroupSpec);
    // 兼容 head 无镜像、推理写在首个 Worker 上的详情数据
    if (!headInference.inferenceSystem && workers[0]) {
      headInference = extractInferenceFromGroup(workers[0]);
    }
    inferenceSystem = headInference.inferenceSystem;
    inferenceService = headInference.inferenceService;
    return {
      masterNodes: spec.headGroupSpec ? [extractNodeConfigFromGroup(spec.headGroupSpec)!].filter(Boolean) : [],
      workerNodes: workers.map((w) => extractNodeConfigFromGroup(w)!).filter(Boolean),
      inferenceSystem,
      inferenceService,
    };
  }

  const primary = workers[0];
  const primaryInference = extractInferenceFromGroup(primary);
  inferenceSystem = primaryInference.inferenceSystem;
  inferenceService = primaryInference.inferenceService;

  return {
    masterNodes: [],
    workerNodes: workers.map((w, index) => {
      const node = extractNodeConfigFromGroup(w)!;
      if (index === 0 && primaryInference.inferenceSystem) {
        // 分布式主 Worker 的推理字段已提到 _inferenceSystem，节点 JSON 不含镜像
      }
      return node;
    }),
    inferenceSystem,
    inferenceService,
  };
}

/** 读取系统盘 UI 字段：Arco Form 带点路径存为嵌套对象，需兼容扁平 key */
function readSystemDiskSizeFromFormFields(
  rawFields: Record<string, unknown>,
  values: MaasSharedFormValues,
  field: string,
): number | undefined {
  const candidates = [get(rawFields, field), get(values, field), (values as Record<string, unknown>)[field]];
  for (const candidate of candidates) {
    if (typeof candidate === 'number' && candidate > 0) return candidate;
  }
  return undefined;
}

/** 提交前：从 masterNodes / workerNodes JSON 与推理/系统盘 UI 字段组装组 spec */
export function assembleGroupSpecsFromNodeFields<T extends MaasSharedFormValues>(
  values: T,
  rawFields: Record<string, unknown>,
  options?: AssembleNodeFieldsOptions,
): T {
  const elevate = options?.elevateSharedEnvAndService === true;
  const embedded = options?.embeddedNodeConfig === true;
  const paths = options?.fieldPaths ?? DEFAULT_MAAS_NODE_GROUP_FIELD_PATHS;
  const runMode =
    (get(values, paths.runMode) as MaasRunMode | undefined) ||
    (get(rawFields, paths.runMode) as MaasRunMode | undefined) ||
    values.runMode ||
    RunMode.Normal;
  const commonEnvPath = paths.commonEnv ?? 'commonEnv';
  const commonEnv = (get(values, commonEnvPath) as typeof values.commonEnv) || values.commonEnv || [];
  const masterNodes = parseNodesField(rawFields[paths.masterNodes] ?? get(values, paths.masterNodes)) as EmbeddedNodeSpec[];
  const workerNodes = parseNodesField(rawFields[paths.workerNodes] ?? get(values, paths.workerNodes)) as EmbeddedNodeSpec[];
  const inferenceSystem = parseFormJsonField<Record<string, unknown>>(rawFields[INFERENCE_SYSTEM_FIELD] ?? values._inferenceSystem);
  const inferenceService = parseFormJsonField(rawFields[INFERENCE_SERVICE_FIELD] ?? values._inferenceService);
  const masterDisk = readSystemDiskSizeFromFormFields(rawFields, values, paths.masterSystemDisk);
  const workerDisk = readSystemDiskSizeFromFormFields(rawFields, values, paths.workerSystemDisk);

  const next = cloneDeep(values) as T;
  if (typeof masterDisk === 'number' && masterDisk > 0) {
    set(next as object, paths.masterSystemDisk, masterDisk);
  }
  if (typeof workerDisk === 'number' && workerDisk > 0) {
    set(next as object, paths.workerSystemDisk, workerDisk);
  }

  if (embedded) {
    const mergeOpts = { commonEnv, elevateSharedEnvAndService: elevate };
    if (runMode === RunMode.PrimarySecondary) {
      Object.assign(next, {
        headGroupSpec: mergeEmbeddedNodeForSubmit(masterNodes[0], { defaultName: 'master', ...mergeOpts }),
        workerGroupSpecs: workerNodes.map((node) => mergeEmbeddedNodeForSubmit(node, { defaultName: 'worker', ...mergeOpts })),
      });
      return next;
    }
    if (runMode === RunMode.Single) {
      Object.assign(next, {
        headGroupSpec: undefined,
        workerGroupSpecs: [mergeEmbeddedNodeForSubmit(workerNodes[0], { defaultName: 'worker', ...mergeOpts })],
      });
      return next;
    }
    Object.assign(next, {
      headGroupSpec: undefined,
      workerGroupSpecs: workerNodes.map((node) => mergeEmbeddedNodeForSubmit(node, { defaultName: 'worker', ...mergeOpts })),
    });
    return next;
  }

  const mergeOpts = { elevateSharedEnvAndService: elevate };

  if (runMode === RunMode.PrimarySecondary) {
    const headGroupSpec = mergeNodeWithInference(masterNodes[0], {
      inferenceSystem,
      inferenceService,
      withInference: true,
      defaultName: 'master',
      commonEnv,
      ...mergeOpts,
    });
    const workerGroupSpecs = workerNodes.map((node) => mergeNodeWithInference(node, { defaultName: 'worker', commonEnv, ...mergeOpts }));
    Object.assign(next, {
      headGroupSpec,
      workerGroupSpecs,
    });
    return next;
  }

  if (runMode === RunMode.Single) {
    const workerGroupSpecs = [
      mergeNodeWithInference(workerNodes[0], {
        inferenceSystem,
        inferenceService,
        withInference: true,
        defaultName: 'worker',
        commonEnv,
        ...mergeOpts,
      }),
    ];
    Object.assign(next, {
      headGroupSpec: undefined,
      workerGroupSpecs,
    });
    return next;
  }

  if (runMode === RunMode.Normal) {
    const workerGroupSpecs = workerNodes.map((node, index) =>
      mergeNodeWithInference(node, {
        inferenceSystem: index === 0 ? inferenceSystem : undefined,
        inferenceService: index === 0 ? inferenceService : undefined,
        withInference: index === 0,
        defaultName: 'worker',
        commonEnv,
        ...mergeOpts,
      }),
    );
    Object.assign(next, {
      headGroupSpec: undefined,
      workerGroupSpecs,
    });
    return next;
  }

  const workerGroupSpecs = workerNodes.map((node, index) =>
    mergeNodeWithInference(node, {
      inferenceSystem: index === 0 ? inferenceSystem : undefined,
      inferenceService: index === 0 ? inferenceService : undefined,
      withInference: index === 0,
      defaultName: 'worker',
      commonEnv,
      ...mergeOpts,
    }),
  );
  Object.assign(next, {
    headGroupSpec: undefined,
    workerGroupSpecs,
  });
  return next;
}

/** 详情/编辑：将 API 组结构转为表单节点 JSON 字段 */
export function buildFormNodeFieldsFromGroupSpec(
  spec: {
    runMode?: MaasRunMode;
    headGroupSpec?: MaasGroupSpec;
    workerGroupSpecs?: MaasGroupSpec[];
  },
  options?: Pick<AssembleNodeFieldsOptions, 'embeddedNodeConfig'>,
): {
  masterNodes: string;
  workerNodes: string;
  _inferenceSystem?: Record<string, unknown>;
  _inferenceService?: unknown;
} {
  const nodeFields = splitGroupsToNodeFields(spec, options);
  return {
    masterNodes: JSON.stringify(nodeFields.masterNodes),
    workerNodes: JSON.stringify(nodeFields.workerNodes),
    _inferenceSystem: nodeFields.inferenceSystem,
    _inferenceService: nodeFields.inferenceService,
  };
}

/** 详情/编辑：组 spec 规范化后拆成 masterNodes / workerNodes 等 UI 字段 */
function prepareGroupSpecForNodeSplit(group?: MaasGroupSpec, envmap?: Record<string, unknown>): MaasGroupSpec | undefined {
  if (!group) return undefined;
  return stripStorageFromGroupSpec(normalizeMaasGroupForNodeFields(group, envmap));
}

/** 应用实例 / 模型实例共用：解析 detail 接口中的节点组为表单 JSON 字段 */
export function parseDetailGroupsToNodeFormFields(
  input: {
    runMode?: MaasRunMode;
    headGroupSpec?: MaasGroupSpec;
    workerGroupSpecs?: MaasGroupSpec[];
    commonEnv?: any[];
  },
  options?: Pick<AssembleNodeFieldsOptions, 'embeddedNodeConfig'>,
): {
  masterNodes: string;
  workerNodes: string;
  _inferenceSystem?: Record<string, unknown>;
  _inferenceService?: unknown;
} {
  const envmap = {};
  if (input.commonEnv?.length > 0) {
    input.commonEnv.forEach((item) => {
      envmap[item.name] = item.value;
    });
  }
  const headGroupSpec = options?.embeddedNodeConfig
    ? input.headGroupSpec
      ? normalizeMaasGroupForNodeFields(input.headGroupSpec, envmap)
      : undefined
    : prepareGroupSpecForNodeSplit(input.headGroupSpec, envmap);
  const workerGroupSpecs = options?.embeddedNodeConfig
    ? (input.workerGroupSpecs?.map((group) => normalizeMaasGroupForNodeFields(group, envmap)).filter(Boolean) as
        | MaasGroupSpec[]
        | undefined)
    : (input.workerGroupSpecs?.map((group) => prepareGroupSpecForNodeSplit(group, envmap)).filter(Boolean) as MaasGroupSpec[] | undefined);

  const nodeFields = buildFormNodeFieldsFromGroupSpec(
    {
      runMode: input.runMode,
      headGroupSpec,
      workerGroupSpecs,
    },
    options,
  );

  return {
    ...nodeFields,
    _inferenceSystem: options?.embeddedNodeConfig ? undefined : normalizeMaasInferenceSystemForForm(nodeFields._inferenceSystem),
  };
}

/** 详情/编辑：系统盘大小写入表单字段 */
export function buildSystemDiskFormFields(sizes: MaasSystemDiskSizes): {
  'master.systemDiskSize'?: number;
  'worker.systemDiskSize'?: number;
} {
  return {
    ...(sizes.headGroupSpec ? { 'master.systemDiskSize': sizes.headGroupSpec } : {}),
    ...(sizes.workers
      ? { 'worker.systemDiskSize': sizes.workers }
      : sizes['workerGroupSpecs[0]']
        ? { 'worker.systemDiskSize': sizes['workerGroupSpecs[0]'] }
        : {}),
  };
}
